import { useState, useRef, useEffect, ChangeEvent } from 'react'
import * as React from 'react'
// import SearchBarIcon from '../../../public/static/icons/search-bar-icon'
// import SearchHistoryIcon, {
//   SearchResultIcon,
// } from '@public/static/icons/search-history-icon'
import { useRouter } from 'next/router'
import addSearchQuery from '@services/api/search/addQuery'
import { useTranslation } from 'react-i18next'
import useWindowDimensions from '@hooks/useWindowDimensions'
import { useDisclosure } from '@nextui-org/react-latest'
import getCourseSuggestions from '@services/api/search/getCourseSuggestions'
import useGetSearchSummary from '@services/react-query/hooks/search/useGetSearchSummary'
import { queryClient } from 'pages/_app'
import { Suggestions } from './interface'
import SearchBarIcon from '@public/static/icons/search-bar-icon'
import FiltersIcon from '@public/static/icons/filter-icon-new'
import dynamic from 'next/dynamic'
import styled from '@emotion/styled'
import { useAuth } from '@contexts/Auth'
const SuggestionsContent = dynamic(
  () => import('./components/SuggestionsContent')
)
const SearchModal = dynamic(() => import('./components/SearchModal'))

type Props = {
  text?: string
  opacity?: string //opacity of the Text
  fontSize?: string //font size of the Text
  textColor?: string
  placeholder?: string
  width?: string
  onFocus?: () => void
  onBlur?: () => void
  handleFilterButton?: () => void
}

const SearchBar: React.FunctionComponent<Props> = (props) => {
  const {
    width = '',
    placeholder,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onFocus = () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onBlur = () => {},
    handleFilterButton,
  } = props

  const router = useRouter()
  const { isAuthLoading } = useAuth()
  const { t } = useTranslation('common')
  const { width: screenWidth } = useWindowDimensions()
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure()
  const { data } = useGetSearchSummary(!isAuthLoading)

  const node = useRef<HTMLDivElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [suggestionTm, setSuggestionTm] = useState<ReturnType<
    typeof setTimeout
  > | null>(null)
  const [suggestions, setSuggestions] = useState<Suggestions>({
    summary: [],
    courses: [],
    authors: [],
  })
  const [searchActive, setSearchActive] = useState(false)

  useEffect(() => {
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])

  useEffect(() => {
    if (data) {
      setSuggestions((prev) => ({
        ...prev,
        summary: data,
      }))
    }
  }, [data])

  useEffect(() => {
    if (router.isReady) {
      setSearchQuery(
        decodeURIComponent((router.query.search || '')?.toString())
      )
    }
    setSearchActive(false)
  }, [router.isReady, router.query.search])

  const handleClick = (e: MouseEvent): void => {
    if (node.current?.contains(e.target as Node)) {
      return
    }
    setSearchActive(false)
    onBlur()
  }

  const handleOnChange = (text: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(text.target.value)

    if (suggestionTm) clearTimeout(suggestionTm)
    setSuggestionTm(
      setTimeout(async () => {
        if (text.target.value.length > 1) {
          const response = await getCourseSuggestions(text.target.value)
          setSuggestions(response)
        }
      }, 400)
    )
    setSearchActive(true)
  }

  const handleOnKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.keyCode === 13) {
      if (searchQuery) {
        router.push(
          {
            pathname: '/courses',
            query: {
              search: `${
                encodeURIComponent(searchQuery?.split('.').join(' ')) || '%20'
              }`,
            },
          },
          undefined,
          { shallow: false }
        )
        if (screenWidth && screenWidth <= 1050) {
          onClose()
        }
        await addSearchQuery(searchQuery)
        queryClient.setQueryData(
          ['search-summary'],
          [
            { _id: new Date().getTime(), text: searchQuery, type: 'history' },
            ...(suggestions?.summary || []),
          ]
        )
        setSearchActive(false)
      }
    }
  }

  return (
    <MainWrapper width={width} ref={node}>
      <SearchBarWrapper>
        <InputWrapper>
          <Icon>
            <SearchBarIcon height="100%" />
          </Icon>
          <Input
            placeholder={
              placeholder ? placeholder : t('header.searchAnyCourse')
            }
            value={searchQuery}
            onClick={() => {
              onFocus()
              setTimeout(() => {
                // compensate for 0.3s animation delay
                setSearchActive(true)
              }, 300)
            }}
            onChange={handleOnChange}
            onKeyDown={handleOnKeyDown}
            // onFocus={onFocus}
            // onBlur={onBlur}
            disabled={screenWidth && screenWidth <= 1050}
            className="disabled:bg-transparent placeholder:font-regular"
          />
          {handleFilterButton && (
            <Icon
              className="!ml-[10px] !mr-[25px]"
              onClick={handleFilterButton}
            >
              <FiltersIcon />
            </Icon>
          )}
          <div
            className="w-[90%] h-5 bg-transparent laptop:hidden flex absolute top-0 left-0"
            onClick={() => {
              if (screenWidth && screenWidth <= 1050) {
                onOpen()
                setSearchActive(true)
              }
            }}
          ></div>
        </InputWrapper>
        {suggestions.summary.length > 0 ||
        suggestions.courses.length > 0 ||
        suggestions.authors.length > 0 ? (
          <span className="w-full z-[999]">
            {searchActive && screenWidth && screenWidth > 1050 && (
              <SuggestionsContent suggestions={suggestions} />
            )}
          </span>
        ) : (
          <></>
        )}
      </SearchBarWrapper>
      {screenWidth && screenWidth <= 1050 ? (
        <SearchModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          onClose={onClose}
          setSearchActive={setSearchActive}
          handleFilterButton={handleFilterButton}
          suggestions={suggestions}
          handleOnChange={handleOnChange}
          searchQuery={searchQuery}
          handleOnKeyDown={handleOnKeyDown}
        />
      ) : (
        <></>
      )}
    </MainWrapper>
  )
}

export default SearchBar

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  width: ${(props) => (props.width ? props.width : '100%')};
  max-width: ${(props) => (props.width ? props.width : '100%')} !important;
`
const SearchBarWrapper = styled.div<{ width: string }>`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 100%;
  /* border: 1px solid rgba(151, 151, 151, 0.25); */
  align-items: center;
  /* justify-content: center; */
  box-sizing: border-box;
  z-index: 2;
  height: 21px;
  position: relative;
  @media (max-width: 1100px) {
    border: 0;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
`
const Icon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.12rem;
  min-width: 1.12rem;
  height: 1rem;
  margin-left: 25px;
  margin-right: 10px;
  cursor: pointer;
  user-select: none;
`
const InputWrapper = styled.div`
  display: flex;
  margin: auto 0;
  padding: 0;
  width: 100%;
  position: relative;
  align-items: center;
`
const Input = styled.input`
  font-size: 0.875rem;
  color: #1a1e3d;
  border: none;
  width: 85%;
  ::placeholder {
    color: #969696;
  }
`
